import CallItem from './components/CallItem';
import { CallTracking } from 'views/components';
import { FILTER_OPTIONS } from './fixtures';
import { ListPanel } from './components/ListPanel';
import ProfessionalItem from './components/ProfessionalItem';
import { config } from 'config';
import { dashboardActions } from 'core/Dashboard';
import isEmpty from 'lodash/isEmpty';
import { isLocked } from 'shared/utils';
import moment from 'moment';
import parseStatus from 'shared/utils/parseStatus';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    Col,
    Row
} from 'reactstrap';
import {
    ContainerFlex,
    PinMessage
} from './styled';
import {
    Loader,
    PageController
} from 'mol-ui';
import React, { useEffect, useState } from 'react';
import {
    useDispatch,
    useLocalstorage,
    useSelector
} from 'hooks';
const {
    dashboardInitialLoad,
    entity,
    professionalStatus
} = config;

const Dashboard = () => {

    const dispatch = useDispatch();
    const { push } = useHistory();
    const { listingProfessional } = useTheme();
    const { t } = useTranslation();
    
    const {
        user
    } = useSelector('account');
    const {
        callsLoading,
        callsPage,
        callsRender,
        professionalsLoading,
        professionalsPage,
        refreshCalls
    } = useSelector('dashboard');

    const showCallHistory = !isLocked(user, 'call_history');
    const professionalsCreate = !isLocked(user, 'professionals_create');
    const showProfessionalsListing = !isLocked(user, 'show_professionals_listing');
    
    const [firstLoad, setFirstLoad] = useState(false);
    const [callsDashboardFilters, setCallsDashboardFilters] = useLocalstorage('dashboard-calls-filters', { type: 'object' });
    const { end, filter, searchValue, start } = callsDashboardFilters ?? {};
    const [professional, setProfessional] = useState({});
    const [search, setSearch] = useState({
        patient: false,
        professional: false
    });
    
    const [professionalsData, setProfessionalsData] = useState([]);
    const [totalProfessionalElements, setTotalProfessionalElements] = useState(0);

    const [patientsData, setPatientsData] = useState([]);
    const [totalPatientElements, setTotalPatientElements] = useState(0);

    useEffect(() => {
        if (callsPage || callsRender) {
            const { data, totalElements } = callsPage.getData();
            setTotalPatientElements(totalElements);
            setPatientsData(data);
        }
    }, [callsPage, callsRender, setPatientsData, setTotalPatientElements]);

    useEffect(() => {
        if (professionalsPage) {
            const { data, totalElements } = professionalsPage.getData();
            setTotalProfessionalElements(totalElements);
            setProfessionalsData(data);
        }
    }, [professionalsPage, setProfessionalsData, setTotalProfessionalElements]);

    useEffect(() => {
        if (!!refreshCalls && showCallHistory) {
            dispatch(dashboardActions.setDashboardRefreshCalls(false));
            dispatch(dashboardActions.getDashboardCalls({
                end_date: moment(end).format().replace('00:00:00', '23:59:59'),
                ...(filter && { filter }),
                ...(searchValue && { search: searchValue }),
                start_date: moment(start).format()
            }));
        }
    }, [refreshCalls, showCallHistory]);

    useEffect(() => {
        if (!firstLoad) {
            setFirstLoad(true);
            if (showCallHistory) {
                if (!end || !start) {
                    const dates = {
                        end: moment().format('YYYY-MM-DD'),
                        start: moment().add(-1, 'day').format('YYYY-MM-DD')
                    };
                    setCallsDashboardFilters(dates);
                }
                const endDate = end ? moment(end) : moment();
                const startDate = start ? moment(start) : moment().add(-1, 'day');
                dispatch(dashboardActions.getDashboardCalls({
                    end_date: moment(endDate).format().replace('00:00:00', '23:59:59'),
                    ...(filter && { filter }),
                    min: dashboardInitialLoad,
                    ...(searchValue && { search: searchValue }),
                    start_date: moment(startDate).format()
                }));
            }
            dispatch(dashboardActions.getDashboardProfessionals());
        }
    }, [firstLoad, setCallsDashboardFilters, showCallHistory]);
    
    const filterChange = (entity, type) => value => {
        switch (entity) {
        case ('patient'): {
            const newPatienDashboardFilters = {
                ...callsDashboardFilters,
                [type]: value
            };
            setCallsDashboardFilters(newPatienDashboardFilters);
            const { end, filter, searchValue, start } = newPatienDashboardFilters ?? {};
            dispatch(dashboardActions.getDashboardCalls({
                end_date: moment(end).format().replace('00:00:00', '23:59:59'),
                ...(filter && { filter }),
                ...(searchValue && { search: searchValue }),
                start_date: moment(start).format()
            }));
            break;
        }
        case ('professional'): {
            const newProfessional = {
                ...professional,
                [type]: value
            };
            setProfessional(newProfessional);
            dispatch(dashboardActions.getDashboardProfessionals(newProfessional));
            break;
        }
        default: break;
        }
        setSearch(search => ({
            ...search,
            [entity]: false
        }));
    };

    const filterChangeDates = (start, end) => {
        dispatch(dashboardActions.getDashboardCalls({
            end_date: moment(end).format().replace('00:00:00', '23:59:59'),
            ...(filter && { filter }),
            ...(searchValue && { search: searchValue }),
            start_date: moment(start).format()
        }));
        setCallsDashboardFilters({
            end: moment(end, 'YYYY-MM-DD'),
            ...(filter && { filter }),
            ...(searchValue && { search: searchValue }),
            start: moment(start, 'YYYY-MM-DD')
        });
    };

    const getEnrollmentTexts = enrollments => enrollments.map(({ name, number }) =>
        `${ name }: ${ number }`).join(', ');

    const handleSearchEnabled = entity => () => {
        setSearch(search => ({
            ...search,
            [entity]: !search[entity]
        }));
    };

    const newPin = () => push(t('/pin/crear'));

    const { patient: patientSearch, professional: professionalSearch } = search;
    
    return (
        <ContainerFlex flexDirection={ showCallHistory ? 'row' : 'column' }>
            {
                !showCallHistory &&
                <Row>
                    <Col>
                        <PinMessage onClick={ newPin }>
                            + { t('Nuevo PIN') }
                        </PinMessage>
                    </Col>
                </Row>
            }
            {
                showCallHistory &&
                <>
                    <CallTracking />
                    <ListPanel
                        endValue={ end }
                        filterDates={ true }
                        filterOptions={ FILTER_OPTIONS.patient }
                        footer={ <PageController controller={ callsPage } /> }
                        handleAddItem={ newPin }
                        handleFilter={ filterChange('patient', 'filter') }
                        handleFilterDates={ filterChangeDates }
                        handleSearch={ filterChange('patient', 'searchValue') }
                        handleSearchEnabled={ handleSearchEnabled('patient') }
                        hideAddItem={ !entity }
                        searchEnabled={ patientSearch }
                        searchValue={ searchValue }
                        showCounter={ totalPatientElements ?? undefined }
                        startValue={ start }
                        title={ t('Consultas') }
                        value={ patientSearch }
                    >
                        <Loader active={ callsLoading } />
                        {
                            !callsLoading && patientsData && patientsData.map((item, index) => (
                                <CallItem
                                    item={ item }
                                    key={ index }
                                />
                            ))
                        }
                    </ListPanel>
                </>
            }
            {
                showProfessionalsListing &&
                <ListPanel
                    filterOptions={ FILTER_OPTIONS.professional }
                    footer={ <PageController controller={ professionalsPage } /> }
                    handleAddItem={ () => push(`${ t('/profesionales/crear') }?from=${ t('/dashboard') }`) }
                    handleFilter={ filterChange('professional', 'filter') }
                    handleSearch={ filterChange('professional', 'search') }
                    handleSearchEnabled={ handleSearchEnabled('professional') }
                    hideAddItem={ !professionalsCreate }
                    searchEnabled={ professionalSearch }
                    showCounter={ totalProfessionalElements ?? undefined }
                    title={ t('Profesionales') }
                    value={ professionalSearch }
                >
                    <Loader active={ professionalsLoading } />
                    {
                        !professionalsLoading && professionalsData && professionalsData.map(({
                            avatar_thumbnail,
                            enrollments,
                            first_name,
                            id,
                            last_name,
                            status,
                            waiting_for_call
                        }) => (
                            <ProfessionalItem
                                avatar={ avatar_thumbnail }
                                color={ listingProfessional }
                                icon="face"
                                key={ id }
                                redirect={ () => push(`${ t('profesionales/ver') }/${ id }?from=${ t('/dashboard') }`) }
                                status={ professionalStatus ? parseStatus(!!waiting_for_call ? 'Waiting' : status) : undefined }
                                text={ !isEmpty(enrollments) ? getEnrollmentTexts(enrollments) : '' }
                                title={ `${ last_name }, ${ first_name }` }
                            />
                        ))
                    }
                </ListPanel>
            }
        </ContainerFlex>
    );

};

Dashboard.propTypes = {

};

export default Dashboard;
